/**
 * Avatar identity — initials and colour — in one place.
 *
 * These were duplicated three ways before: `initialsFrom` in the panel, a separate `initials` in the
 * participants popover, and no colour at all in the participant list. So the same person could appear
 * as a coloured circle on a message and a grey one in the list two inches away, which reads as two
 * different people rather than one.
 */

/**
 * Two letters from a name. Falls back to the first two characters when there is only one word, so a
 * single-word title still produces a circle rather than a lone letter floating in one.
 */
export function initialsFrom(value: string | undefined): string {
  const words = (value || "").trim().split(/[\s,]+/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  // First letters of the leading words, which is the reference design's rule. Deliberately not
  // first-plus-last: "Jean Claude Van Damme" reads as JC there and JD here, and the two were visibly
  // disagreeing between the message list and the participant list.
  return words.map(word => word[0]).join("").slice(0, 2).toUpperCase()
}

/**
 * A stable colour per person, so the same author is the same colour on every message, in the
 * participant list and in the grid preview.
 *
 * <p>Hashed from the name rather than assigned by index: an index colour shifts whenever the list
 * reorders or somebody is added, which turns the avatar from an identity into decoration.</p>
 *
 * <p>Every entry is dark enough to carry white text at 700 weight — that is the constraint on adding
 * to this palette, not variety. A pastel here would produce an unreadable circle. Muddy oranges and
 * browns are out on purpose: they read as "washed out" beside the blue-grey panel rather than as an
 * identity.</p>
 */
const AVATAR_COLORS = [
  "#0f766e", "#1d4ed8", "#6d28d9", "#be185d",
  "#b91c1c", "#15803d", "#0e7490", "#c2410c"
]

export function avatarColorFor(name: string | undefined): string {
  const value = (name || "?").trim()

  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(i)) | 0
  }

  // The plain `hash % 8` this replaced was not distributing: 31 ≡ -1 (mod 8), so the bucket collapsed
  // to an alternating sum of character codes and near-identical names landed on the same colour.
  // "Support GorillaWorks", "Alan Moon" and "Contact 1" were all one shade on screen.
  // A final avalanche pushes the high bits down before the modulo, which is the part that was missing.
  hash ^= hash >>> 16
  hash = Math.imul(hash, 0x45d9f3b)
  hash ^= hash >>> 16
  hash = Math.imul(hash, 0x45d9f3b)
  hash ^= hash >>> 16

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}
